import React, { useEffect, useRef, useState } from 'react';
import { AvForm } from 'availity-reactstrap-validation';
import SaveSubmitButton from '../../form/buttons/SaveSubmitButton';
import {
  closeModalSafely,
  handleSubmit,
} from './create-or-edit-printer-config.service';
import ModalSheet from '../modal-sheet/modal-sheet.component';
import { FormattedMessage, useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { fetchPrinterConfigs } from '../../../../redux/store/store.actions';
import FormField from '../../form/fields/FormField';
import {
  setIsCreatePrinterConfigModalOpen,
  setIsEditPrinterConfigModalOpen,
} from '../../../../redux/modal/modal.actions';
import { Col, Row, Button } from 'reactstrap';
import DropdownField from '../../form/fields/DropdownField';
import { basePostQuery } from '../../../../utility/sb-api/sb-api.service';
import {
  characterSets,
  ORDER_TYPE_CATERING,
  ORDER_TYPE_DELIVERY,
  ORDER_TYPE_INHOUSE,
  ORDER_TYPE_PICKUP,
  ORDER_TYPE_SELF_SERVICE,
} from '../../../../utility/constants';
import LabeledToggle from '../../form/LabeledToggle';
import {
  capitalize,
  getLocalizedName,
} from '../../../../utility/util/util.service';
import SettingLine from '../../form/fields/SettingLine';
import LayoutTagsField from '../../form/fields/LayoutTagsField';
import { notificationFormattedMessage } from '../../../../utility/notification/notification.service';

const TCP_ADDRESS_REGEX = /^((\d{1,3}\.){3}\d{1,3}|localhost|([a-zA-Z0-9-]+\.)*[a-zA-Z0-9-]+)(:([1-9][0-9]{0,4}))?$/;

const CreateOrEditPrinterConfigModal = ({
  isCreatePrinterConfigModalOpen,
  isEditPrinterConfigModalOpen,
  createPrinterConfigModalData,
  editPrinterConfigModalData,
  itemCategories,
  bridges,

  setIsCreatePrinterConfigModalOpen,
  setIsEditPrinterConfigModalOpen,
  fetchPrinterConfigs,
}) => {
  const intl = useIntl();
  const [targetPrinterConfig, setTargetPrinterConfig] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [tcpAddressOptions, setTcpAddressOptions] = useState([]);
  const [
    enableManualTcpAddressInput,
    setEnableManualTcpAddressInput,
  ] = useState(false);
  const [selectedBridgeId, setSelectedBridgeId] = useState(null);
  const [printerConfigInterface, setPrinterConfigInterface] = useState('usb');
  const [testOrderType, setTestOrderType] = useState();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isOpen = isCreatePrinterConfigModalOpen || isEditPrinterConfigModalOpen;
  const isEditMode = isEditPrinterConfigModalOpen;
  const { printerConfig, onSuccess, type } =
    (isEditMode
      ? editPrinterConfigModalData
      : {
          ...createPrinterConfigModalData,
          printerConfig: {
            breakLine: 'WORD',
            lineCharacter: '-',
            fontSize: 'small',
            buzzCount: '0',
            isActive: true,
            characterSet: 'PC437_USA',
          },
        }) || {};
  const formRef = useRef(null);

  const closeModal = () =>
    isCreatePrinterConfigModalOpen
      ? setIsCreatePrinterConfigModalOpen(false)
      : setIsEditPrinterConfigModalOpen(false);

  useEffect(() => {
    if (isOpen) {
      setTargetPrinterConfig((isEditMode && printerConfig) || printerConfig);
      setPrinterConfigInterface(printerConfig?.interface || 'usb');
      setSelectedBridgeId(printerConfig?.bridge?.id || null);
    } else if (!isSubmitting) {
      // reset modal on closing
      setTargetPrinterConfig({});
      setSelectedBridgeId(null);
    }
  }, [
    createPrinterConfigModalData,
    editPrinterConfigModalData,
    isEditMode,
    isOpen,
  ]);

  const _closeModalSafely = () => closeModalSafely(isSubmitting, closeModal);

  const _handleSubmit = (event, errors, values) => {
    handleSubmit(
      event,
      errors,
      {
        ...values,
        buzzCount: parseInt(values.buzzCount || '0'),
        isActive: !!values?.isActive,
        removeSpecialCharacters: !!values?.removeSpecialCharacters,
        upsideDown: !!values?.upsideDown,
        groupByItemCategories: !!values?.groupByItemCategories,
        interface: printerConfigInterface,
        tcpAddress:
          printerConfigInterface === 'tcp' ? values?.tcpAddress : undefined,
        layoutTags: targetPrinterConfig?.layoutTags || [],
        macAddress: targetPrinterConfig?.macAddress || undefined,
      },
      targetPrinterConfig,
      isEditMode,
      setIsSubmitting,
      setTargetPrinterConfig,
      closeModal,
      fetchPrinterConfigs,
      isEditMode ? printerConfig?.type : type,
    );
  };

  const handleSendTestPrintJob = () => {
    basePostQuery(
      `printer-configs/${targetPrinterConfig?.id}/test-print-job`,
      {
        orderType: testOrderType,
        printerConfig: { ...targetPrinterConfig, allowedItemCategories: [] },
      },
      null,
      null,
      'testPrintError',
    ).then(() => {
      /* notificationFormattedMessage('primary', 'testPrintSuccess', {
        printerName: targetPrinterConfig?.name,
      }); */
    });
  };

  const handleScanBridgeNetwork = () => {
    if (!selectedBridgeId) {
      setTcpAddressOptions([]); // without a selected Bridge, we cannot fetch anything. Just set an empty array.
      return;
    }

    if (tcpAddressOptions.length > 0) {
      return; // already fetched, just return
    }

    setIsLoading(true);
    basePostQuery(
      `bridges/${selectedBridgeId}/scan-network`,
      { port: '9100' },
      null,
      null,
      null,
      true,
      { timeout: 300 * 1000 }, // 5min timeout, this request can take a while
    )
      .then(({ data }) => {
        setTcpAddressOptions(data || []);
      })
      .catch((error) => {
        console.error('Error scanning network:', error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <ModalSheet
      isOpen={isOpen}
      closeModal={_closeModalSafely}
      title={intl.formatMessage({
        id: isEditMode
          ? 'editPrinterConfigTitle'
          : 'createNewPrinterConfigTitle',
      })}
      footer={
        <div className="align-content-center">
          <SaveSubmitButton
            data-testid="printerConfigSaveButton"
            className="m-auto"
            disabled={isSubmitting}
            isSubmitting={isSubmitting}
            onClick={() => formRef?.current.handleSubmit()}
          />
        </div>
      }
    >
      <div className="px-1">
        <AvForm
          ref={formRef}
          onSubmit={_handleSubmit}
          disabled={isSubmitting}
          className="mt-1"
        >
          <LabeledToggle
            disabled={isSubmitting}
            wrapperClassName="d-flex align-content-center justify-content-between mb-1"
            headingMessageId="isActiveLabel"
            headingClassName="d-block black"
            propertyName="isActive"
            targetObject={targetPrinterConfig}
          />
          <SettingLine
            headingClassName="align-self-center d-block font-medium-1 black"
            headingMessageId="name"
            subHeadingMessageId="nameSubtitle"
            marginClass="my-1"
            component={
              <FormField
                groupClassName="w-100"
                data-testid="printerConfigNameInput"
                propertyName="name"
                object={targetPrinterConfig}
                hideFloatingLabel
                required
              />
            }
          />
          <hr />
          {(type === 'RegoPrinterConfig' ||
            printerConfig?.type === 'RegoPrinterConfig') && (
            <SettingLine
              headingClassName="align-self-center d-block font-medium-1 black"
              headingMessageId="cloudIdTitle"
              subHeadingMessageId="cloudIdSubtitle"
              marginClass="my-1"
              component={
                <FormField
                  hideFloatingLabel
                  groupClassName="w-100"
                  data-testid="printerConfigCloudIdInput"
                  propertyName="cloudId"
                  object={targetPrinterConfig}
                  disabled={isEditPrinterConfigModalOpen}
                  required
                />
              }
            />
          )}
          {(type === 'BridgePrinterConfig' ||
            printerConfig?.type === 'BridgePrinterConfig') && (
            <SettingLine
              headingClassName="align-self-center d-block font-medium-1 black"
              headingMessageId="Bridge"
              subHeadingMessageId="bridgeSubTitle"
              marginClass="my-1"
              component={
                <DropdownField
                  groupClassName="w-100"
                  isClearable={false}
                  isMulti={false}
                  options={
                    bridges?.filter(
                      (bridge) => bridge?.enablePrinterApp === true,
                    ) || []
                  }
                  propertyName="bridgeId"
                  getOptionValue={(bridge) => bridge?.id}
                  values={targetPrinterConfig?.bridge?.id}
                  emptyValue={null}
                  getOptionLabel={(bridge) => bridge?.name}
                  disabled={isEditMode}
                  onChange={(bridge) => setSelectedBridgeId(bridge?.id)}
                  required
                />
              }
            />
          )}
          {((type === 'BridgePrinterConfig' && !isEditMode) ||
            (printerConfig?.type === 'BridgePrinterConfig' && isEditMode)) && (
            <>
              <SettingLine
                headingClassName="align-self-center d-block font-medium-1 black"
                headingMessageId="interfaceTitle"
                subHeadingMessageId="interfaceSubtitle"
                marginClass="my-1"
                component={
                  <DropdownField
                    groupClassName="w-100"
                    isClearable={false}
                    isMulti={false}
                    options={['tcp', 'usb']}
                    propertyName="interface"
                    emptyValue={null}
                    object={targetPrinterConfig}
                    values={printerConfigInterface}
                    onChange={(value) => setPrinterConfigInterface(value)}
                    getOptionLabel={(printerConfigInterface) =>
                      printerConfigInterface.toUpperCase()
                    }
                    required
                  />
                }
              />
              {printerConfigInterface === 'tcp' && (
                <>
                  <LabeledToggle
                    disabled={isSubmitting}
                    wrapperClassName="d-flex align-content-center justify-content-between mt-1 mb-2"
                    headingMessageId="enableManualTcpAddressTitle"
                    subHeadingMessageId="enableManualTcpAddressSubTitle"
                    headingClassName="d-block black"
                    checked={enableManualTcpAddressInput}
                    onChange={() =>
                      setEnableManualTcpAddressInput(
                        !enableManualTcpAddressInput,
                      )
                    }
                  />
                  <SettingLine
                    headingClassName="align-self-center d-block font-medium-1 black"
                    headingMessageId="tcpAddressLabel"
                    subHeadingMessageId="tcpAddressSubTitle"
                    marginClass="my-1"
                    component={
                      enableManualTcpAddressInput ? (
                        <FormField
                          hideFloatingLabel
                          groupClassName="w-100"
                          data-testid="printerConfigTcpAddressInput"
                          propertyName="tcpAddress"
                          object={targetPrinterConfig}
                          //validate={{ pattern: { value: TCP_ADDRESS_REGEX } }}
                          //required
                        />
                      ) : (
                        <DropdownField
                          groupClassName="w-100"
                          isClearable={false}
                          isMulti={false}
                          options={
                            tcpAddressOptions?.length
                              ? tcpAddressOptions
                              : // on edit, we don't want to scan the network every time as it takes some time. So "fake" the options by adding a single entry in the options array.
                              targetPrinterConfig?.tcpAddress
                              ? [
                                  {
                                    ip: targetPrinterConfig.tcpAddress,
                                    mac: targetPrinterConfig.macAddress,
                                  },
                                ]
                              : []
                          }
                          propertyName="tcpAddress"
                          onMenuOpen={handleScanBridgeNetwork}
                          isLoading={isLoading}
                          emptyValue={''}
                          object={targetPrinterConfig}
                          getOptionLabel={({ ip, name }) =>
                            ip + (name ? ` (${name})` : '')
                          }
                          getOptionValue={
                            (option) => /*option?.hostname ||*/ option?.ip // use hostname if available // TODO: update validation regex in printer-app first!!
                          }
                          loadingMessage={() =>
                            intl.formatMessage({
                              id: 'tcpAddressScanningLabel',
                            })
                          }
                          onChange={(tcpAddress) => {
                            setTargetPrinterConfig((prev) => ({
                              ...prev,
                              tcpAddress: tcpAddress?.ip,
                              macAddress: tcpAddress?.mac,
                            }));
                          }}
                          required
                        />
                      )
                    }
                  />
                </>
              )}
            </>
          )}
          {!isEditMode && <hr />}
          {((type === 'BridgePrinterConfig' && !isEditMode) ||
            (printerConfig?.type === 'BridgePrinterConfig' && isEditMode)) && (
            <>
              <SettingLine
                headingClassName="align-self-center d-block font-medium-1 black"
                headingMessageId="brandLabel"
                subHeadingMessageId="brandSubTitle"
                marginClass="my-1"
                component={
                  <DropdownField
                    groupClassName="w-100"
                    isClearable={false}
                    isMulti={false}
                    options={['epson', 'tanca', 'star', 'daruma', 'custom']}
                    propertyName="brand"
                    object={targetPrinterConfig}
                    values={targetPrinterConfig?.brand}
                    getOptionLabel={(brand) => capitalize(brand)}
                    onChange={(brand) => {
                      setTargetPrinterConfig((prev) => ({
                        ...prev,
                        brand,
                      }));
                    }}
                    required
                  />
                }
              />
            </>
          )}
          <SettingLine
            headingClassName="align-self-center d-block font-medium-1 black"
            headingMessageId="characterSetTitle"
            subHeadingMessageId="characterSetSubtitle"
            marginClass="my-1"
            component={
              <DropdownField
                groupClassName="w-100"
                isClearable={false}
                isMulti={false}
                options={characterSets || []}
                propertyName="characterSet"
                emptyValue={null}
                object={targetPrinterConfig}
                values={targetPrinterConfig?.characterSet}
                getOptionLabel={(characterSet) => characterSet?.label}
                getOptionValue={(characterSet) => characterSet?.value}
                onChange={(characterSet) => {
                  setTargetPrinterConfig((prev) => ({
                    ...prev,
                    characterSet: characterSet?.value,
                  }));
                }}
                required
              />
            }
          />

          <LabeledToggle
            disabled={isSubmitting}
            wrapperClassName="d-flex align-content-center justify-content-between mt-1 mb-2"
            headingMessageId="removeSpecialCharactersLabel"
            subHeadingMessageId="removeSpecialCharactersSubTitle"
            headingClassName="d-block black"
            propertyName="removeSpecialCharacters"
            targetObject={targetPrinterConfig}
          />
          {/* <LabeledToggle
            disabled={isSubmitting}
            wrapperClassName="d-flex align-content-center justify-content-between mb-2"
            headingMessageId="upsideDownLabel"
            subHeadingMessageId="upsideDownLabelSubTitle"
            headingClassName="d-block black"
            propertyName="upsideDown"
            targetObject={targetPrinterConfig} 
          /> */}
          <SettingLine
            headingClassName="align-self-center d-block font-medium-1 black"
            headingMessageId="lineCharacterLabel"
            subHeadingMessageId="lineCharacterSubTitle"
            marginClass="my-1"
            component={
              <FormField
                hideFloatingLabel
                groupClassName="w-100"
                data-testid="printerConfigNameInput"
                propertyName="lineCharacter"
                object={targetPrinterConfig}
              />
            }
          />
          <SettingLine
            headingClassName="align-self-center d-block font-medium-1 black"
            headingMessageId="breakLineLabel"
            subHeadingMessageId="breakLineSubTitle"
            marginClass="my-1"
            component={
              <DropdownField
                groupClassName="w-100"
                isClearable={false}
                isMulti={false}
                options={['NONE', 'CHARACTER', 'WORD']}
                propertyName="breakLine"
                object={targetPrinterConfig}
                values={targetPrinterConfig?.breakLine}
                onChange={(breakLine) => {
                  setTargetPrinterConfig((prev) => ({
                    ...prev,
                    breakLine,
                  }));
                }}
                required
              />
            }
          />
          <SettingLine
            headingClassName="align-self-center d-block font-medium-1 black"
            headingMessageId="fontSizeTitle"
            subHeadingMessageId="fontSizeSubTitle"
            marginClass="my-1"
            component={
              <DropdownField
                groupClassName="w-100"
                isMulti={false}
                isClearable={false}
                options={[
                  {
                    value: 'small',
                    label: intl.formatMessage({ id: 'smallFontType' }),
                  },
                  {
                    value: 'medium',
                    label: intl.formatMessage({ id: 'mediumFontType' }),
                  },
                  {
                    value: 'large',
                    label: intl.formatMessage({ id: 'largeFontType' }),
                  },
                ]}
                propertyName="fontSize"
                object={targetPrinterConfig}
                placeholderMessageId="selectFontSizeLabel"
                getOptionLabel={(fontSize) => capitalize(fontSize?.label)}
                getOptionValue={(fontSize) => fontSize?.value}
                values={targetPrinterConfig?.fontSize}
                onChange={(fontSize) => {
                  setTargetPrinterConfig((prev) => ({
                    ...prev,
                    fontSize: fontSize?.value,
                  }));
                }}
                required
              />
            }
          />
          <SettingLine
            headingClassName="align-self-center d-block font-medium-1 black"
            headingMessageId="allowedItemCategoriesTitle"
            subHeadingMessageId="allowedItemCategoriesSubTitle"
            marginClass="my-1"
            component={
              <DropdownField
                groupClassName="w-100"
                isClearable={true}
                isMulti={true}
                options={itemCategories || []}
                propertyName="allowedItemCategoryIds"
                getOptionValue={(category) => category?.id}
                values={targetPrinterConfig?.allowedItemCategories?.map(
                  (category) => category?.id,
                )}
                onChange={(allowedItemCategories) => {
                  setTargetPrinterConfig((prev) => ({
                    ...prev,
                    allowedItemCategories,
                  }));
                }}
                emptyValue={null}
                getOptionLabel={(category) =>
                  getLocalizedName(category, intl.locale)
                }
              />
            }
          />
          <LabeledToggle
            disabled={isSubmitting}
            wrapperClassName="d-flex align-content-center justify-content-between mt-1 mb-2"
            headingMessageId="groupByCategoriesTitle"
            subHeadingMessageId="groupByCategoriesSubTitle"
            headingClassName="d-block black"
            propertyName="groupByItemCategories"
            targetObject={targetPrinterConfig}
          />
          <span className="d-block font-medium-1 black">
            <FormattedMessage id="layoutTagsLabel" />
          </span>
          <span className="d-block font-small-2 mb-1">
            <FormattedMessage id="layoutTagsSubTitle" />
          </span>
          <LayoutTagsField
            layoutTags={targetPrinterConfig?.layoutTags}
            setTargetPrinterConfig={setTargetPrinterConfig}
          />

          {isEditMode && (
            <>
              <hr />
              <Row>
                <Col className="pr-50 mt-1" xs={6}>
                  <DropdownField
                    isMulti={false}
                    options={[
                      ORDER_TYPE_INHOUSE,
                      ORDER_TYPE_PICKUP,
                      ORDER_TYPE_DELIVERY,
                      ORDER_TYPE_CATERING,
                      ORDER_TYPE_SELF_SERVICE,
                    ]}
                    propertyName="orderType"
                    labelMessageId="testPrintSelectOrderTypeLabel"
                    onChange={(orderType) => {
                      setTestOrderType(orderType);
                    }}
                  />
                </Col>
                <Col className="pl-50 mt-1" xs={6}>
                  <Button
                    color="primary"
                    className="w-100"
                    onClick={handleSendTestPrintJob}
                  >
                    <FormattedMessage id="testPrintSubmitLabel" />
                  </Button>
                </Col>
              </Row>
            </>
          )}
        </AvForm>
      </div>
    </ModalSheet>
  );
};

const mapStateToProps = (state) => ({
  isCreatePrinterConfigModalOpen: state.modals.isCreatePrinterConfigModalOpen,
  isEditPrinterConfigModalOpen: state.modals.isEditPrinterConfigModalOpen,
  createPrinterConfigModalData: state.modals.createPrinterConfigModalData,
  editPrinterConfigModalData: state.modals.editPrinterConfigModalData,
  itemCategories: state.store.itemCategories,
  connectors: state.store.connectors,
  bridges: state.store.bridges,
});

export default connect(mapStateToProps, {
  setIsCreatePrinterConfigModalOpen,
  setIsEditPrinterConfigModalOpen,
  fetchPrinterConfigs,
})(CreateOrEditPrinterConfigModal);
